import { resolveRuleSet, validationRuleFieldSelector } from "../rules";
import { validateSideName, resolveMaxLengthByScript } from "../side_name_validation";
import { CreateSideNameValidationOptions, MaxLengthByScript, ValidationResult } from "../types";

export function createSideNameValidation(options: CreateSideNameValidationOptions): { init: () => void, syncValidationState: () => void } {
  /** 書体ラジオとペアになっている入力フィールドの validateSideName の結果のみ保持。*/
  const lastValidationResults = new Map<HTMLInputElement, boolean>();
  /** pageshow リスナーの多重登録防止 */
  let historyRestoreBound = false;

  /**
   * バリデーションメッセージの表示処理
   * @param inputEl 入力フィールド
   * @param validatedResult validateSideName の返り値
   */
  function renderMessage(inputEl: HTMLInputElement, validatedResult: ValidationResult): void {
    const messageEl = inputEl.closest(".js-validation-field")?.querySelector(".js-validation-message");
    if (!messageEl) {
      console.error("Missing messageEl: renderMessage");
      return;
    }
    messageEl.textContent = validatedResult.ok ? "" : validatedResult.message;
  }

  /**
   * options.onResultsChange に lastValidationResults の評価結果を渡して実行するラッパー
   */
  function notifyResultsChange(): void {
    if (!options.onResultsChange) return;
    const allOk = [...lastValidationResults.values()].every((ok) => ok === true);
    options.onResultsChange(allOk);
  }

  /**
   * validationInput の組み立て、バリデーションの実行、メッセージの表示を担う
   * @param inputEl 入力フィールド
   */
  function runValidation(inputEl: HTMLInputElement): void {
    if (options.isActiveInput && !options.isActiveInput(inputEl)) return;
    const ruleSet = resolveRuleSet(inputEl.dataset.validationRule);
    if (!ruleSet) {
      console.error("Missing ruleSet");
      lastValidationResults.set(inputEl, false);
      notifyResultsChange();
      return;
    }

    const validationInput = options.buildInput(inputEl);
    if (!validationInput) {
      lastValidationResults.set(inputEl, false);
      notifyResultsChange();
      return;
    }

    const validationResult = validateSideName(validationInput, ruleSet);

    renderMessage(inputEl, validationResult);

    lastValidationResults.set(inputEl, validationResult.ok);
    notifyResultsChange();
  }

  /**
   * 関数にデバウンスを付与して返すファクトリー
   * @param fn 元の関数
   * @param ms デバウンスの待ち時間
   * @returns デバウンスを付与した関数
   */
  function debounceFactory(fn: () => void, ms: number): () => void {
    let timerId: ReturnType<typeof setTimeout> | undefined = undefined;
    return () => {
      clearTimeout(timerId);
      timerId = setTimeout(fn, ms);
    };
  }

  /**
   * 入力フィールドに3つのイベントリスナーを仕掛ける
   *  - compositionstart: IME 入力開始
   *  - compositionend:   IME 入力終了
   *  - input:            IME を利用しない入力
   * @param inputEl バリデーション対象の入力フィールド
   */
  function bindInputEvent(inputEl: HTMLInputElement): void {
    let isComposing = false;
    const debounceRunValidation = debounceFactory(() => runValidation(inputEl), 300);

    inputEl.addEventListener("compositionstart", () => {
      isComposing = true;
    });
    inputEl.addEventListener("compositionend", () => {
      isComposing = false;
      runValidation(inputEl);
    });
    inputEl.addEventListener("input", () => {
      if (isComposing) return;
      debounceRunValidation();
    });
  }

  /** 
   * 上限文字数メッセージの組み立て
   * @param max 英文・和文ごとの上限文字数 
   */
  function formatMaxLengthDisplayLabel(max: MaxLengthByScript): string {
    if (max.ja === 0) {
      return `英数字のみ:${max.en}文字まで`;
    }
    return `英数字のみ:${max.en}文字まで/日本語含む:${max.ja}文字まで`;
  }

  /**
   * 名入れ内容付近の上限文字数表示の切り替え
   * @param inputEl 入力フィールド
   */
  function updateMaxLengthDisplay(inputEl: HTMLInputElement): void {
    const validationInput = options.buildInput(inputEl);
    if (!validationInput) return;
    const displayEl = options.findMaxLengthDisplayEl(inputEl);
    if(!displayEl) {
      console.warn("Missing max length display Element");
      return;
    }

    const max = resolveMaxLengthByScript(validationInput.code, validationInput.font);
    displayEl.textContent = formatMaxLengthDisplayLabel(max);
  }

  /**
   * 書体ラジオに change イベントリスナーを仕掛ける
   * @param inputEl バリデーション対象の入力フィールド
   */
  function bindFontChangeEvents(inputEl: HTMLInputElement): void {
    const fontRadios = options.findFontRadios(inputEl);
    fontRadios?.forEach((radio) => {
      radio.addEventListener("change", () => {
        runValidation(inputEl);
        updateMaxLengthDisplay(inputEl);
      });
    });
  }

  function revalidateAfterHistoryRestore(): void {
    for (const inputEl of lastValidationResults.keys()) {
      runValidation(inputEl);
      updateMaxLengthDisplay(inputEl);
    }
  }

  /**
   * ブラウザバック時に value が復元される前にバリデーションが走り empty エラーになり、
   * その後ブラウザがフォーム値を復元しても input が飛ばずメッセージが残る、という不具合の対策。
   * pageshow 後（フォーム復元後になり得る次タスク）で再バリデーションする処理を実装。
   */
  function bindHistoryRestoreRevalidation(): void {
    if (historyRestoreBound) return;
    historyRestoreBound = true;
    window.addEventListener("pageshow", () => {
      window.setTimeout(revalidateAfterHistoryRestore, 0);
    });
  }
  
  function clearMessage(inputEl: HTMLInputElement): void {
    renderMessage(inputEl, { ok: true });
  }
  
  function syncValidationState(): void {
    const inputFields = document.querySelectorAll<HTMLInputElement>(validationRuleFieldSelector());
    inputFields.forEach((inputEl) => {
      const active = !options.isActiveInput || options.isActiveInput(inputEl);
      if (!active) {
        lastValidationResults.delete(inputEl);
        clearMessage(inputEl);
        return;
      }
      if (!lastValidationResults.has(inputEl)) {
        lastValidationResults.set(inputEl, false);
      }
      runValidation(inputEl);
      updateMaxLengthDisplay(inputEl);
    });
    notifyResultsChange();
  }

  /**
   * 初期化処理
   * data-validation-rule を持たない（＝ RULE_SET_BY_VALIDATION_RULE 未登録の）input は対象外
   * 同スコープ内に書体ラジオがない input も対象外
   * isActiveInput が false の input は init 時の validate / display をスキップするが、bind は行う
   */
  function init(): void {
    const inputFields = document.querySelectorAll<HTMLInputElement>(validationRuleFieldSelector());
    if (!inputFields.length) return;
    inputFields.forEach((inputEl) => {
      if (!options.hasFont(inputEl)) {
        console.warn("side_name_validation: 書体選択がないためスキップ");
        return;
      }
      lastValidationResults.set(inputEl, false);
      const isActive = !options.isActiveInput || options.isActiveInput(inputEl);
      if (isActive) {
        runValidation(inputEl);
      }
      bindInputEvent(inputEl);
      bindFontChangeEvents(inputEl);
      if (isActive) {
        updateMaxLengthDisplay(inputEl);
      }
    });
    bindHistoryRestoreRevalidation();
  }

  return { init, syncValidationState };
}
